import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO, { trackEvent } from '@/hooks/useSEO';

/**
 * Founder DNA Assessment — pre-qualification quiz scoring fit for SKI Axis Cohort 1.
 * Inspired by FI's Entrepreneur DNA test (15 years of research).
 * Scoring: 7 dimensions, 5 levels each. Total max = 35.
 *   28+ = "Cohort 1 Match" (gold) — fast-track CTA to apply
 *   21–27 = "Strong Foundations" (silver) — apply with strong narrative
 *   14–20 = "Build Your Edge" (bronze) — masterclass first
 *   <14  = "Early Stage" (stone) — newsletter / forum first
 */

const QUESTIONS = [
  {
    id: 'stage',
    eyebrow: 'Venture Stage',
    h: 'Where is your venture today?',
    options: [
      { v: 1, label: 'Just an idea — exploring & validating', sub: 'No prototype, no users yet' },
      { v: 2, label: 'Pre-product — defining problem & customer', sub: 'Customer interviews underway' },
      { v: 3, label: 'MVP / early prototype', sub: 'Building, but no revenue' },
      { v: 4, label: 'Live product with early traction', sub: 'First customers / users' },
      { v: 5, label: 'Growing revenue — preparing to scale', sub: 'Product-market fit signals' },
    ],
  },
  {
    id: 'commitment',
    eyebrow: 'Founder Commitment',
    h: 'How much time can you commit weekly?',
    options: [
      { v: 1, label: '<5 hrs · Just exploring', sub: 'Side curiosity' },
      { v: 2, label: '5–10 hrs · Evenings & weekends', sub: 'Building on the side' },
      { v: 3, label: '10–20 hrs · Serious side project', sub: 'Could go full-time soon' },
      { v: 4, label: '20–40 hrs · Almost full-time', sub: 'Quarter-life pivot underway' },
      { v: 5, label: '40+ hrs · Full-time founder', sub: 'All-in — no plan B' },
    ],
  },
  {
    id: 'uae_intent',
    eyebrow: 'UAE Intent',
    h: 'What is your UAE jurisdiction strategy?',
    options: [
      { v: 1, label: 'Curious — UAE is on my radar', sub: 'No concrete plan yet' },
      { v: 2, label: 'Researching free zones & options', sub: 'Comparing IFZA / DMCC / DSO' },
      { v: 3, label: 'Decision made — entering UAE in 12 months', sub: 'Timeline locked' },
      { v: 4, label: 'Already incorporated in UAE', sub: 'Trade licence active' },
      { v: 5, label: 'UAE-native operating + scaling globally', sub: 'Dubai HQ established' },
    ],
  },
  {
    id: 'team',
    eyebrow: 'Team',
    h: 'What is your founding team composition?',
    options: [
      { v: 1, label: 'Solo — looking for co-founder', sub: 'No team yet' },
      { v: 2, label: 'Solo — but committed to going alone', sub: 'Will hire as needed' },
      { v: 3, label: '2-person founding team', sub: 'Complementary skills' },
      { v: 4, label: '3+ founders, no employees yet', sub: 'Full founding team' },
      { v: 5, label: 'Founding team + early hires (2–10)', sub: 'Growing org' },
    ],
  },
  {
    id: 'capital',
    eyebrow: 'Capital Strategy',
    h: 'What is your funding plan in the next 12 months?',
    options: [
      { v: 1, label: 'Bootstrapping — no plan to raise', sub: 'Self-funded indefinitely' },
      { v: 2, label: 'Bootstrapping — open to angels later', sub: 'Reactive raise' },
      { v: 3, label: 'Pre-seed in 6–12 months', sub: 'Building runway narrative' },
      { v: 4, label: 'Actively raising pre-seed / seed', sub: 'Pitch deck ready' },
      { v: 5, label: 'Already raised (>$100K) — scaling', sub: 'Capital deployed' },
    ],
  },
  {
    id: 'governance',
    eyebrow: 'Governance Maturity',
    h: 'How would you describe your governance posture?',
    options: [
      { v: 1, label: "I haven't thought about governance yet", sub: 'Compliance is future-me\'s problem' },
      { v: 2, label: 'I know I\'ll need it eventually', sub: 'Researching basics' },
      { v: 3, label: 'I have core docs (incorp, NDAs, contracts)', sub: 'Foundational stack in place' },
      { v: 4, label: 'I have a governance framework + compliance', sub: 'AML / KYC defined' },
      { v: 5, label: 'I work with embassies / institutional capital', sub: 'ESG/SDG-aligned' },
    ],
  },
  {
    id: 'why_skiaxis',
    eyebrow: 'Fit',
    h: 'What is the single biggest thing you need from SKI Axis?',
    options: [
      { v: 5, label: 'Cross-border governance & UAE entry expertise', sub: 'Our north-star value' },
      { v: 5, label: 'Investor introductions in MENA + APAC', sub: 'Capital network access' },
      { v: 4, label: 'Structured curriculum & faculty mentorship', sub: 'Founder acceleration' },
      { v: 4, label: 'AI scoring & institutional credibility', sub: 'Investor-ready signal' },
      { v: 3, label: 'Just exploring — not sure yet', sub: 'Window-shopping' },
    ],
  },
];

const tierFor = (score) => {
  if (score >= 28) return { tier: 'gold', label: 'Cohort 1 Match', headline: "You're built for Cohort 1.", desc: 'Your stage, commitment, and UAE intent are exactly what we built Cohort 1 for. Apply now — limited seats, fast-track review for top-quartile DNA scores.' };
  if (score >= 21) return { tier: 'silver', label: 'Strong Foundations', headline: 'Strong founder DNA. Apply with a sharp narrative.', desc: 'You have the makings of a Cohort 1 founder. Review our masterclass first, then submit your application — show us how the cohort accelerates your specific gap.' };
  if (score >= 14) return { tier: 'bronze', label: 'Build Your Edge', headline: "You're early — but the door is open.", desc: 'Spend 6–8 weeks deepening one or two dimensions (commitment, governance, or UAE intent). Start with our free masterclass and re-take the assessment when ready.' };
  return { tier: 'stone', label: 'Early Stage', headline: 'Welcome to the journey.', desc: 'Cohort 1 is too advanced for where you are right now. Subscribe to the SKI Axis newsletter, attend the Invest UAE Forum 2026, and re-apply once you have product clarity.' };
};

const STYLES = `
.fdna-root{min-height:100vh;background:#FAF7F0;color:#0D1B2A;font-family:'DM Sans',-apple-system,sans-serif;padding:64px 48px 96px;}
.fdna-nav{position:sticky;top:0;z-index:50;background:rgba(250,247,240,.92);backdrop-filter:blur(20px);border-bottom:1px solid rgba(200,168,75,.18);padding:18px 48px;display:flex;justify-content:space-between;align-items:center;margin:-64px -48px 48px;}
.fdna-back{background:none;border:none;font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#3A4A5C;cursor:pointer;}
.fdna-back:hover{color:#C8A84B;}
.fdna-logo{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#C8A84B;}
.fdna-logo em{font-style:italic;font-weight:300;color:#0D1B2A;}
.fdna-inner{max-width:840px;margin:0 auto;}
.fdna-eyebrow{font-size:10px;font-weight:700;letter-spacing:.32em;text-transform:uppercase;color:#C8A84B;margin-bottom:16px;display:flex;align-items:center;gap:12px;}
.fdna-eyebrow::before{content:'';width:32px;height:1px;background:#C8A84B;}
.fdna-h1{font-family:'Cormorant Display',serif;font-size:clamp(48px,5.4vw,72px);font-weight:700;line-height:1;letter-spacing:-.025em;margin:0 0 18px;}
.fdna-h1 em{font-style:italic;color:#C8A84B;}
.fdna-sub{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;font-size:clamp(18px,1.8vw,22px);color:#3A4A5C;line-height:1.55;max-width:600px;margin:0 0 40px;}
.fdna-progress{height:3px;background:rgba(200,168,75,.15);border-radius:50px;overflow:hidden;margin-bottom:28px;}
.fdna-progress-bar{height:100%;background:#C8A84B;transition:width .4s cubic-bezier(.16,1,.3,1);}
.fdna-step{font-size:11px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:#7A8190;margin-bottom:14px;}
.fdna-step strong{color:#C8A84B;}
.fdna-q-eyebrow{font-size:10px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:10px;}
.fdna-q-h{font-family:'Cormorant Display',serif;font-size:clamp(28px,3.4vw,40px);font-weight:700;line-height:1.15;letter-spacing:-.015em;margin:0 0 28px;}
.fdna-options{display:flex;flex-direction:column;gap:10px;}
.fdna-option{background:#fff;border:1px solid rgba(200,168,75,.18);border-left:3px solid transparent;padding:18px 22px;cursor:pointer;text-align:left;transition:all .2s cubic-bezier(.16,1,.3,1);display:flex;align-items:flex-start;gap:14px;}
.fdna-option:hover{border-color:rgba(200,168,75,.45);border-left-color:#C8A84B;background:#FFFCF6;transform:translateX(4px);}
.fdna-option.selected{border-color:#C8A84B;border-left-color:#C8A84B;background:#FFFCF6;box-shadow:0 4px 16px rgba(200,168,75,.12);}
.fdna-option-marker{width:22px;height:22px;border-radius:50%;border:1.5px solid rgba(200,168,75,.4);flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all .2s;}
.fdna-option.selected .fdna-option-marker{background:#C8A84B;border-color:#C8A84B;}
.fdna-option.selected .fdna-option-marker::after{content:'';width:8px;height:8px;border-radius:50%;background:#0D1B2A;}
.fdna-option-text{flex:1;}
.fdna-option-label{font-size:14.5px;font-weight:600;color:#0D1B2A;line-height:1.4;margin-bottom:3px;}
.fdna-option-sub{font-size:12px;color:#7A8190;font-weight:400;line-height:1.5;}
.fdna-actions{display:flex;justify-content:space-between;align-items:center;margin-top:32px;gap:12px;}
.fdna-btn{font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:14px 28px;border-radius:50px;cursor:pointer;border:1px solid;transition:all .25s;}
.fdna-btn.primary{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fdna-btn.primary:hover{background:#0D1B2A;color:#C8A84B;}
.fdna-btn.primary:disabled{opacity:.35;cursor:not-allowed;}
.fdna-btn.line{background:transparent;color:#0D1B2A;border-color:rgba(13,27,42,.2);}
.fdna-btn.line:hover{border-color:#C8A84B;color:#C8A84B;}

/* RESULT */
.fdna-result{max-width:780px;margin:0 auto;text-align:center;}
.fdna-tier{display:inline-flex;align-items:center;gap:10px;font-size:10px;font-weight:800;letter-spacing:.28em;text-transform:uppercase;padding:10px 22px;border-radius:50px;margin-bottom:24px;border:1px solid;}
.fdna-tier.gold{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fdna-tier.silver{background:rgba(200,168,75,.12);color:#C8A84B;border-color:rgba(200,168,75,.45);}
.fdna-tier.bronze{background:rgba(122,100,50,.08);color:#7A6432;border-color:rgba(122,100,50,.32);}
.fdna-tier.stone{background:rgba(122,129,144,.08);color:#3A4A5C;border-color:rgba(122,129,144,.3);}
.fdna-score-ring{width:200px;height:200px;border-radius:50%;background:conic-gradient(#C8A84B var(--p),rgba(200,168,75,.12) 0);display:flex;align-items:center;justify-content:center;margin:0 auto 32px;position:relative;}
.fdna-score-ring::before{content:'';position:absolute;inset:14px;border-radius:50%;background:#FAF7F0;}
.fdna-score-num{position:relative;z-index:1;font-family:'Cormorant Display',serif;font-size:72px;font-weight:700;color:#0D1B2A;line-height:1;font-style:italic;}
.fdna-score-num em{color:#C8A84B;font-size:32px;}
.fdna-result-h{font-family:'Cormorant Display',serif;font-size:clamp(40px,4.8vw,60px);font-weight:700;line-height:1.05;letter-spacing:-.02em;margin:0 0 18px;}
.fdna-result-h em{font-style:italic;color:#C8A84B;}
.fdna-result-desc{font-size:16px;line-height:1.75;color:#3A4A5C;max-width:600px;margin:0 auto 40px;}
.fdna-result-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:14px;margin-bottom:40px;}
.fdna-result-share{padding:24px 28px;background:#fff;border:1px solid rgba(200,168,75,.18);border-radius:4px;}
.fdna-share-h{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#7A8190;margin-bottom:14px;}
.fdna-share-row{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;}
.fdna-share-btn{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:9px 16px;border-radius:50px;border:1px solid rgba(200,168,75,.32);background:transparent;color:#0D1B2A;cursor:pointer;text-decoration:none;}
.fdna-share-btn:hover{background:#C8A84B;border-color:#C8A84B;color:#0D1B2A;}
@media(max-width:640px){.fdna-root{padding:48px 24px 80px;}.fdna-nav{margin:-48px -24px 32px;padding:14px 24px;}.fdna-actions{flex-direction:column;align-items:stretch;}.fdna-btn{width:100%;text-align:center;}.fdna-options{gap:8px;}}
`;

export default function FounderDNAQuiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({}); // { [qid]: optionIndex }

  useSEO({
    title: "Founder DNA Assessment · 90-Second Startup Fit Test | SKI AXIS Cohort 1",
    description: "Take the SKI AXIS Founder DNA Assessment — a 7-question diagnostic based on 15+ years of governance and founder research. See if you are built for Cohort 1, Dubai's governance-first startup accelerator. Free, 90 seconds.",
    keywords: "founder DNA test, startup accelerator fit test, Dubai accelerator application, founder readiness UAE, startup accelerator cohort Dubai, apply for accelerator UAE, governance accelerator Dubai",
    canonical: "/founder-dna",
    ogImage: "/og-dna.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Quiz",
      "name": "SKI AXIS Founder DNA Assessment",
      "about": "Startup founder readiness for Dubai governance-first accelerator programme",
      "educationalAlignment": { "@type": "AlignmentObject", "alignmentType": "educationalSubject", "targetName": "Startup Accelerator Eligibility" },
      "publisher": { "@type": "Organization", "name": "SKI AXIS", "url": "https://skiaxis.com" },
    },
  });

  const total = QUESTIONS.length;
  const isResult = step >= total;
  const q = QUESTIONS[step];
  const score = QUESTIONS.reduce((s, qq) => {
    const idx = answers[qq.id];
    return s + (idx !== undefined ? qq.options[idx].v : 0);
  }, 0);
  const result = isResult ? tierFor(score) : null;
  const ringPct = (score / 35) * 360;

  const select = (idx) => {
    setAnswers({ ...answers, [q.id]: idx });
  };

  const next = () => {
    if (step < total) {
      // Track DNA completion when reaching result
      if (step === total - 1) {
        const finalScore = QUESTIONS.reduce((s, qq) => {
          const idx = { ...answers, [q.id]: answers[q.id] }[qq.id];
          return s + (idx !== undefined ? qq.options[idx].v : 0);
        }, 0);
        trackEvent('founder_dna_complete', { score: finalScore, tier: tierFor(finalScore).tier });
      }
      setStep(step + 1);
    }
  };
  const prev = () => { if (step > 0) setStep(step - 1); };
  const restart = () => { setAnswers({}); setStep(0); };

  if (isResult && result) {
    const shareText = `My SKI Axis Founder DNA Score: ${score}/35 — "${result.label}". Take the assessment for SKI Axis Cohort 1:`;
    const shareUrl = typeof window !== 'undefined' ? window.location.origin + '/founder-dna' : 'https://skiaxis.com/founder-dna';
    return (
      <div className="fdna-root" data-testid="fdna-result-page">
        <style>{STYLES}</style>
        <div className="fdna-nav">
          <button className="fdna-back" onClick={() => navigate('/')} data-testid="fdna-result-back">← SKI Axis</button>
          <div className="fdna-logo">SKI <em>AXIS</em></div>
        </div>
        <div className="fdna-inner fdna-result">
          <span className={`fdna-tier ${result.tier}`} data-testid="fdna-tier">{result.label}</span>
          <div className="fdna-score-ring" style={{ '--p': `${ringPct}deg` }} data-testid="fdna-score-ring">
            <div className="fdna-score-num" data-testid="fdna-score-num">{score}<em>/35</em></div>
          </div>
          <h1 className="fdna-result-h">{result.headline.split(' ').slice(0, -1).join(' ')} <em>{result.headline.split(' ').slice(-1)[0]}</em></h1>
          <p className="fdna-result-desc">{result.desc}</p>
          <div className="fdna-result-actions">
            {result.tier === 'gold' && (
              <button className="fdna-btn primary" onClick={() => navigate('/cohort-1/apply?dna=' + score)} data-testid="fdna-apply-fast">Fast-Track Apply Cohort 1 →</button>
            )}
            {result.tier === 'silver' && (
              <>
                <button className="fdna-btn primary" onClick={() => navigate('/cohort-1/apply?dna=' + score)} data-testid="fdna-apply">Apply for Cohort 1 →</button>
                <button className="fdna-btn line" onClick={() => navigate('/masterclass')} data-testid="fdna-masterclass">View Masterclass</button>
              </>
            )}
            {result.tier === 'bronze' && (
              <>
                <button className="fdna-btn primary" onClick={() => navigate('/masterclass')} data-testid="fdna-masterclass-bronze">Start Masterclass →</button>
                <button className="fdna-btn line" onClick={restart} data-testid="fdna-retake">Re-take Assessment</button>
              </>
            )}
            {result.tier === 'stone' && (
              <>
                <button className="fdna-btn primary" onClick={() => navigate('/events/invest-uae-forum-2026')} data-testid="fdna-forum">Join the UAE Forum →</button>
                <button className="fdna-btn line" onClick={restart} data-testid="fdna-retake-stone">Re-take Assessment</button>
              </>
            )}
          </div>
          <div className="fdna-result-share">
            <div className="fdna-share-h">Share Your DNA Score</div>
            <div className="fdna-share-row">
              <a className="fdna-share-btn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="fdna-share-linkedin">LinkedIn</a>
              <a className="fdna-share-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="fdna-share-whatsapp">WhatsApp</a>
              <a className="fdna-share-btn" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="fdna-share-x">X</a>
              <button type="button" className="fdna-share-btn" onClick={() => navigator.clipboard?.writeText(shareUrl)} data-testid="fdna-share-copy">Copy Link</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selected = answers[q.id];
  const progressPct = ((step) / total) * 100;  return (
    <div className="fdna-root" data-testid="fdna-quiz-page">
      <style>{STYLES}</style>
      <div className="fdna-nav">
        <button className="fdna-back" onClick={() => navigate('/')} data-testid="fdna-quiz-back">← SKI Axis</button>
        <div className="fdna-logo">SKI <em>AXIS</em></div>
      </div>
      <div className="fdna-inner">
        {step === 0 && (
          <>
            <div className="fdna-eyebrow">Founder DNA Assessment</div>
            <h1 className="fdna-h1">Are you built for <em>Cohort 1?</em></h1>
            <p className="fdna-sub">A 7-question diagnostic — built on 15+ years of governance & founder research at SKI APAC. Get your fit score in 90 seconds.</p>
          </>
        )}
        <div className="fdna-progress" data-testid="fdna-progress"><div className="fdna-progress-bar" style={{ width: `${progressPct}%` }} /></div>
        <div className="fdna-step">Step <strong>{step + 1}</strong> of {total}</div>
        <div className="fdna-q-eyebrow">{q.eyebrow}</div>
        <h2 className="fdna-q-h" data-testid={`fdna-q-${q.id}`}>{q.h}</h2>
        <div className="fdna-options" role="radiogroup">
          {q.options.map((o, i) => (
            <button
              key={i}
              type="button"
              className={`fdna-option ${selected === i ? 'selected' : ''}`}
              onClick={() => select(i)}
              role="radio"
              aria-checked={selected === i}
              data-testid={`fdna-opt-${q.id}-${i}`}
            >
              <span className="fdna-option-marker" />
              <span className="fdna-option-text">
                <span className="fdna-option-label">{o.label}</span>
                <span className="fdna-option-sub">{o.sub}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="fdna-actions">
          <button className="fdna-btn line" onClick={prev} disabled={step === 0} style={step === 0 ? { visibility: 'hidden' } : undefined} data-testid="fdna-prev">← Back</button>
          <button className="fdna-btn primary" onClick={next} disabled={selected === undefined} data-testid="fdna-next">{step === total - 1 ? 'See My DNA Score →' : 'Next →'}</button>
        </div>
      </div>
    </div>
  );
}
